import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3, TrendingUp, DollarSign, Car, Shield, Clock,
  Download, RefreshCw, AlertTriangle, Target, Zap, FileText, Wrench,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar,
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MIX_COLORS = ['#3b82f6', '#8b5cf6', '#14b8a6'];
const TREND_COLORS = { gross_pvr: '#3b82f6', fi_pvr: '#8b5cf6', lead_conversion_pct: '#14b8a6' };

const formatUsd = (n) => {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
};

const formatPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const formatDate = (ds) => formatDateTimeMdY(ds, '—');

function FormulaBlock({ label, formula }) {
  return (
    <div className="bg-white/[0.03] border border-white/10 px-4 py-3">
      <p className="text-[10px] font-['Manrope'] text-white/35 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm font-['Manrope'] text-white/80 font-mono">{formula}</p>
    </div>
  );
}

function MetricGauge({ title, value, display, benchmark, target, unit = '', subtitle, alert }) {
  const numeric = Number(value);
  const safe = Number.isFinite(numeric) ? numeric : 0;
  const targetNum = Number(target);
  const pct = Number.isFinite(targetNum) && targetNum > 0 ? Math.min(100, (safe / targetNum) * 100) : 0;
  const below = benchmark != null && benchmark !== '' && safe < Number(benchmark);
  const shown = display != null && display !== '' ? display : `${safe}${unit}`;
  return (
    <div className={`bg-white/5 border p-5 ${alert ? 'border-amber-500/40' : 'border-white/10'}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <p className="text-xs font-['Manrope'] text-white/50 uppercase tracking-wide">{title}</p>
        {alert && <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />}
      </div>
      <p className={`text-2xl font-['Unbounded'] font-bold ${below ? 'text-amber-300' : 'text-white'}`}>
        {shown}
      </p>
      {subtitle && <p className="text-[11px] font-['Manrope'] text-white/35 mt-1 whitespace-normal break-words">{subtitle}</p>}
      {target != null && (
        <div className="mt-3">
          <Progress value={Number.isFinite(pct) ? pct : 0} className="h-1.5 bg-white/10 [&>div]:bg-blue-500" />
          <p className="text-[10px] font-['Manrope'] text-white/30 mt-1">Target: {target}{unit}</p>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onRefresh }) {
  return (
    <div className="text-center py-20 px-6 border border-dashed border-white/10">
      <BarChart3 className="w-12 h-12 text-white/20 mx-auto mb-4" />
      <h3 className="text-lg font-['Unbounded'] text-white mb-2">Operations Intelligence</h3>
      <p className="text-sm font-['Manrope'] text-white/45 max-w-md mx-auto mb-6">
        Your dealership P&L dashboard pulls live data from deals, inventory, leads, and document flow.
        Create deals and move them through the pipeline to see gross PVR, F&I penetration, and holding costs.
      </p>
      <button
        type="button"
        onClick={onRefresh}
        className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-['Manrope'] font-semibold"
      >
        Load Economics Data
      </button>
    </div>
  );
}

export default function OperationsPage() {
  const { token, hasSection } = useAuth();
  const canOpenVehicles = hasSection('dashboard');
  const canOpenService = hasSection('service');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [periodDays, setPeriodDays] = useState(30);
  const [report, setReport] = useState(null);
  const [dataUpdated, setDataUpdated] = useState(null);
  const [whatIf, setWhatIf] = useState({
    conversion_lift_pct: 5,
    days_supply_reduction: 7,
    fi_penetration_lift_pct: 10,
  });
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [whatIfLoading, setWhatIfLoading] = useState(false);

  const fetchEconomics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/operations/economics`, {
        headers,
        params: { days: periodDays },
      });
      setReport(res.data.report);
      setDataUpdated(res.data.data_last_updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load operations economics');
    } finally {
      setLoading(false);
    }
  }, [headers, periodDays]);

  useEffect(() => { fetchEconomics(); }, [fetchEconomics]);

  const runWhatIf = useCallback(async () => {
    setWhatIfLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/operations/what-if`,
        { ...whatIf, period_days: periodDays },
        { headers },
      );
      setWhatIfResult(res.data.scenario);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'What-if simulation failed');
    } finally {
      setWhatIfLoading(false);
    }
  }, [headers, whatIf, periodDays]);

  useEffect(() => {
    const t = setTimeout(runWhatIf, 400);
    return () => clearTimeout(t);
  }, [whatIf, periodDays, runWhatIf]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/operations/economics/generate`,
        { period_days: periodDays, export_pdf: true },
        { headers },
      );
      const reportId = res.data.report_id;
      const dl = await axios.get(
        `${BACKEND_URL}/api/admin/operations/economics/reports/${reportId}/download`,
        { headers, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([dl.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `operations_economics_${reportId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Economics report exported');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const es = report?.executive_summary;
  const km = report?.key_metrics;
  const mix = report?.revenue_mix;
  const inventory = km?.inventory;
  const aging = report?.inventory_aging || [];
  const serviceCharges = report?.service_charges || [];
  const periodServiceGross = km?.service_absorption?.period_service_gross ?? 0;

  const mixData = mix ? [
    { name: 'Front Gross', value: mix.front_gross, pct: mix.front_pct },
    { name: 'F&I / Back-End', value: mix.fi_gross, pct: mix.fi_pct },
    { name: 'Service & Parts', value: mix.service_gross, pct: mix.service_pct },
  ] : [];

  const trendSource = (report?.trends?.last_90_days?.length
    ? report.trends.last_90_days
    : report?.trends?.last_30_days) || [];

  const barTrend = trendSource.length > 0
    ? trendSource.map((s) => ({
      date: formatDateMdY(s.captured_at || Date.now()),
      gross_pvr: s.gross_pvr,
      fi_pvr: s.fi_pvr,
      conversion: s.lead_conversion_pct,
      total_gross: s.total_gross_period ?? ((s.gross_pvr || 0) * (s.completed_sales_count || 1)),
    }))
    : [{
      date: 'Current',
      gross_pvr: es?.gross_pvr || 0,
      fi_pvr: es?.fi_pvr || 0,
      conversion: km?.lead_funnel?.conversion_pct || 0,
      total_gross: es?.total_gross_period || 0,
    }];

  if (loading && !report) {
    return (
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <div className="p-12 text-center text-white/40 font-['Manrope']">Loading operations intelligence...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        <EmptyState onRefresh={fetchEconomics} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="operations-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white" data-testid="operations-page-title">
            Operations
          </h1>
          <p className="text-sm font-['Manrope'] text-white/40 mt-1">
            Dealership economics — gross PVR, F&I, inventory velocity, and platform impact
          </p>
          {dataUpdated && (
            <p className="text-[11px] font-['Manrope'] text-white/30 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Data last updated: {formatDate(dataUpdated)}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
            className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] [&>option]:bg-gray-900"
          >
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            type="button"
            onClick={fetchEconomics}
            className="flex items-center gap-1.5 px-3 py-2 border border-white/10 text-white/60 hover:text-white text-sm font-['Manrope']"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exporting}
            data-testid="export-economics-pdf"
            className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : <><span className="lg:hidden">Export PDF</span><span className="hidden lg:inline">Export Full Economics Report (PDF)</span></>}
          </button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="bg-gradient-to-br from-blue-500/10 via-violet-500/5 to-transparent border border-white/10 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-amber-400" />
          <h2 className="text-sm font-['Unbounded'] font-semibold text-white uppercase tracking-wide">Executive Summary</h2>
        </div>
        <p className="text-base font-['Manrope'] text-white/80 mb-4">{es?.headline}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-[11px] text-white/40 font-['Manrope']">Gross PVR (Front + Back)</p>
            <p className="text-xl font-['Unbounded'] text-white">{formatUsd(es?.gross_pvr)}</p>
            <p className="text-[10px] text-white/30">Benchmark $3,000–$3,500</p>
          </div>
          <div>
            <p className="text-[11px] text-white/40 font-['Manrope']">F&I PVR</p>
            <p className="text-xl font-['Unbounded'] text-violet-300">{formatUsd(es?.fi_pvr)}</p>
            <p className="text-[10px] text-white/30">Target $1,400–$2,200+</p>
          </div>
          <div>
            <p className="text-[11px] text-white/40 font-['Manrope']">Completed Sales</p>
            <p className="text-xl font-['Unbounded'] text-white">{es?.completed_sales ?? 0}</p>
          </div>
          <div>
            <p className="text-[11px] text-white/40 font-['Manrope']">Period Total Gross</p>
            <p className="text-xl font-['Unbounded'] text-emerald-300">{formatUsd(es?.total_gross_period)}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
        {/* Revenue Mix */}
        <div className="xl:col-span-1 bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-1 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-blue-400" /> Revenue Mix
          </h3>
          <p className="text-[11px] font-['Manrope'] text-white/35 mb-4">{mix?.note}</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={mixData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {mixData.map((_, i) => <Cell key={i} fill={MIX_COLORS[i % MIX_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatUsd(v)} contentStyle={{ background: '#111', border: '1px solid #333' }} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#aaa' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-center">
            {mixData.map((m, i) => (
              <div key={m.name}>
                <p className="text-[10px] text-white/40">{m.name}</p>
                <p className="text-sm font-['Unbounded']" style={{ color: MIX_COLORS[i] }}>{formatPct(m.pct)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MetricGauge
            title="Avg Days Supply"
            value={inventory?.avg_days_supply ?? 0}
            target={inventory?.target_days_supply ?? 35}
            subtitle={`${inventory?.vehicles_over_45_days ?? 0} units over 45 days`}
            alert={(inventory?.avg_days_supply ?? 0) > 45}
          />
          <MetricGauge
            title="F&I Penetration"
            value={Number(km?.fi_penetration?.penetration_pct) || 0}
            display={formatPct(km?.fi_penetration?.penetration_pct)}
            target={100}
            subtitle={`Avg F&I PVR ${formatUsd(km?.fi_penetration?.avg_fi_pvr)}`}
          />
          <MetricGauge
            title="Service Absorption"
            value={Number(km?.service_absorption?.absorption_pct) || 0}
            display={formatPct(km?.service_absorption?.absorption_pct)}
            target={100}
            subtitle={km?.service_absorption?.label || ''}
          />
          <MetricGauge
            title="Lead-to-Sale Conversion"
            value={Number(km?.lead_funnel?.conversion_pct) || 0}
            display={formatPct(km?.lead_funnel?.conversion_pct)}
            target={100}
            subtitle={`${km?.lead_funnel?.inquiries ?? 0} inquiries → ${km?.lead_funnel?.deals_created ?? 0} deals`}
          />
          <MetricGauge
            title="E-Sign Completion"
            value={Number(km?.document_efficiency?.esign_completion_rate_pct) || 0}
            display={formatPct(km?.document_efficiency?.esign_completion_rate_pct)}
            target={100}
            subtitle={`Funding velocity: ${km?.document_efficiency?.avg_funding_velocity_days ?? '—'} days`}
          />
          <MetricGauge
            title="Compliance Risk Score"
            value={km?.document_efficiency?.compliance_risk_score ?? 0}
            subtitle="Lower is better — driven by doc turnaround"
            alert={(km?.document_efficiency?.compliance_risk_score ?? 0) > 30}
          />
        </div>
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-400" /> PVR Trend
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={barTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 10 }} />
              <YAxis tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v) => formatUsd(v)} contentStyle={{ background: '#111', border: '1px solid #333' }} />
              <Line type="monotone" dataKey="gross_pvr" stroke={TREND_COLORS.gross_pvr} name="Gross PVR" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="fi_pvr" stroke={TREND_COLORS.fi_pvr} name="F&I PVR" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-4 flex items-center gap-2">
            <Target className="w-4 h-4 text-teal-400" /> Conversion & Gross Generated
          </h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={barTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
              <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis yAxisId="left" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: '#111', border: '1px solid #333' }}
                formatter={(value, name) => (
                  name === 'Conversion %' ? [`${Number(value).toFixed(1)}%`, name] : [formatUsd(value), name]
                )}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="conversion" fill="#14b8a6" name="Conversion %" radius={[2, 2, 0, 0]} />
              <Bar yAxisId="right" dataKey="total_gross" fill="#6366f1" name="Gross Generated" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Service Revenue */}
      <div className="bg-white/5 border border-white/10 p-5 mb-6">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-1 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-teal-400" /> Service Revenue
        </h3>
        <p className="text-[11px] font-['Manrope'] text-white/35 mb-4">
          Live quotes, including customer-approved quotes, feed Service Absorption above.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="bg-white/[0.03] border border-white/10 p-4">
            <p className="text-[11px] text-white/40 font-['Manrope']">Period Service Gross</p>
            <p className="text-xl font-['Unbounded'] text-teal-300">{formatUsd(periodServiceGross)}</p>
          </div>
          <div className="bg-white/[0.03] border border-white/10 p-4">
            <p className="text-[11px] text-white/40 font-['Manrope']">Recorded Quotes</p>
            <p className="text-xl font-['Unbounded'] text-white">{serviceCharges.length}</p>
          </div>
        </div>
        {serviceCharges.length === 0 ? (
          <p className="text-sm text-white/40 font-['Manrope'] py-4 text-center border border-dashed border-white/10">
            No quotes recorded yet. Add a quote from a Service Request to feed live data.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-['Manrope']">
              <thead>
                <tr className="text-left text-white/40 border-b border-white/10">
                  <th className="py-2 pr-4">Customer</th>
                  <th className="py-2 pr-4">Parts</th>
                  <th className="py-2 pr-4">Labor</th>
                  <th className="py-2 pr-4">Total</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2">Recorded</th>
                </tr>
              </thead>
              <tbody>
                {serviceCharges.map((row) => (
                  <tr key={row.id} className="border-b border-white/5">
                    <td className="py-2.5 pr-4 text-white/80">
                      {canOpenService && row.service_request_id ? (
                        <Link
                          to={`/admin/service-requests/${row.service_request_id}`}
                          className="text-blue-300 hover:text-blue-200 hover:underline"
                        >
                          {row.customer_name || '—'}
                        </Link>
                      ) : (
                        row.customer_name || '—'
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-white/60">{formatUsd(row.total_parts_charge)}</td>
                    <td className="py-2.5 pr-4 text-white/60">{formatUsd(row.total_labor_charge)}</td>
                    <td className="py-2.5 pr-4 text-teal-300 font-semibold">{formatUsd(row.total_service_charge)}</td>
                    <td className="py-2.5 pr-4 text-white/50 capitalize">{(row.source || row.status || '').replace(/_/g, ' ')}</td>
                    <td className="py-2.5 text-white/40 text-xs">{formatDate(row.operations_recorded_at || row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inventory Aging */}
      <div className="bg-white/5 border border-white/10 p-5 mb-6">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-1 flex items-center gap-2">
          <Car className="w-4 h-4 text-amber-400" /> Inventory Aging
        </h3>
        <p className="text-[11px] font-['Manrope'] text-white/35 mb-4">
          Vehicles over 45 days highlighted — est. ${inventory?.daily_floor_plan_cost ?? 20}/day floor plan bleed
        </p>
        {aging.length === 0 ? (
          <p className="text-sm text-white/40 font-['Manrope'] py-6 text-center">No active inventory units</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-['Manrope']">
              <thead>
                <tr className="text-left text-white/40 border-b border-white/10">
                  <th className="py-2 pr-4">Vehicle</th>
                  <th className="py-2 pr-4">Stock #</th>
                  <th className="py-2 pr-4">Price</th>
                  <th className="py-2 pr-4">Days on Lot</th>
                  <th className="py-2 pr-4">Holding Cost</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {aging.slice(0, 12).map((row) => (
                  <tr
                    key={row.vehicle_id}
                    className={`border-b border-white/5 ${row.aging_alert ? 'bg-amber-500/10' : ''}`}
                  >
                    <td className="py-2.5 pr-4 text-white/80">
                      {canOpenVehicles && row.vehicle_id ? (
                        <Link
                          to={`/admin/vehicles/${row.vehicle_id}`}
                          className="text-blue-300 hover:text-blue-200 hover:underline"
                        >
                          {row.year} {row.make} {row.model}
                        </Link>
                      ) : (
                        <>{row.year} {row.make} {row.model}</>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-white/50">{row.stock_number}</td>
                    <td className="py-2.5 pr-4">{formatUsd(row.price)}</td>
                    <td className={`py-2.5 pr-4 font-semibold ${row.aging_alert ? 'text-amber-300' : 'text-white/70'}`}>
                      {row.days_on_lot}d
                    </td>
                    <td className="py-2.5 pr-4 text-red-300/80">{formatUsd(row.estimated_holding_cost)}</td>
                    <td className="py-2.5">
                      {row.aging_alert ? (
                        <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30">
                          AGING
                        </span>
                      ) : (
                        <span className="text-white/40 text-xs">{row.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {inventory?.is_estimated && (
          <p className="text-[10px] text-amber-400/70 mt-3 font-['Manrope']">{inventory.label}</p>
        )}
      </div>

      {/* What-If Simulator */}
      <div className="bg-white/5 border border-violet-500/20 p-6 mb-6">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-violet-400" /> What-If Simulator
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="text-xs font-['Manrope'] text-white/50 block mb-2">
              Improve lead-to-sale conversion by {whatIf.conversion_lift_pct}%
            </label>
            <Slider
              value={[whatIf.conversion_lift_pct]}
              onValueChange={([v]) => setWhatIf((p) => ({ ...p, conversion_lift_pct: v }))}
              max={25}
              step={1}
              className="[&_[role=slider]]:bg-violet-500"
            />
          </div>
          <div>
            <label className="text-xs font-['Manrope'] text-white/50 block mb-2">
              Reduce avg days supply by {whatIf.days_supply_reduction} days
            </label>
            <Slider
              value={[whatIf.days_supply_reduction]}
              onValueChange={([v]) => setWhatIf((p) => ({ ...p, days_supply_reduction: v }))}
              max={30}
              step={1}
            />
          </div>
          <div>
            <label className="text-xs font-['Manrope'] text-white/50 block mb-2">
              Increase F&I penetration by {whatIf.fi_penetration_lift_pct}%
            </label>
            <Slider
              value={[whatIf.fi_penetration_lift_pct]}
              onValueChange={([v]) => setWhatIf((p) => ({ ...p, fi_penetration_lift_pct: v }))}
              max={30}
              step={1}
            />
          </div>
        </div>
        {whatIfLoading ? (
          <p className="text-white/40 text-sm">Recalculating...</p>
        ) : whatIfResult && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-violet-500/5 border border-violet-500/20">
            <div>
              <p className="text-[10px] text-white/40">Monthly Profit Lift</p>
              <p className="text-lg font-['Unbounded'] text-emerald-300">
                {formatUsd(whatIfResult.outputs?.total_profit_lift_monthly)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-white/40">Annual Profit Lift</p>
              <p className="text-lg font-['Unbounded'] text-white">
                {formatUsd(whatIfResult.outputs?.total_profit_lift_annual)}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-white/40">Holding Cost Savings</p>
              <p className="text-lg font-['Unbounded'] text-teal-300">
                {formatUsd(whatIfResult.outputs?.holding_cost_savings_monthly)}/mo
              </p>
            </div>
            <div>
              <p className="text-[10px] text-white/40">Added Unit Sales</p>
              <p className="text-lg font-['Unbounded'] text-white">
                +{whatIfResult.outputs?.added_unit_sales ?? 0}
              </p>
            </div>
            <p className="col-span-full text-sm font-['Manrope'] text-white/60">{whatIfResult.recommendation}</p>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-400" /> Operating Recommendations
          </h3>
          <div className="space-y-3">
            {(report.recommendations || []).map((rec, i) => (
              <div
                key={i}
                className={`p-4 border ${
                  rec.priority === 'high' ? 'border-red-500/30 bg-red-500/5' :
                  rec.priority === 'medium' ? 'border-amber-500/30 bg-amber-500/5' :
                  'border-white/10'
                }`}
              >
                <p className="text-[10px] uppercase tracking-wide text-white/40 mb-1">
                  {rec.category} · {rec.priority}
                </p>
                <p className="text-sm font-['Unbounded'] text-white mb-1">{rec.title}</p>
                <p className="text-xs font-['Manrope'] text-white/55">{rec.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Formulas */}
        <div className="bg-white/5 border border-white/10 p-5">
          <h3 className="text-sm font-['Unbounded'] font-semibold text-white mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4 text-white/50" /> Economic Model
          </h3>
          <div className="space-y-2">
            {Object.entries(report.formulas || {}).map(([key, formula]) => (
              <FormulaBlock key={key} label={key.replace(/_/g, ' ')} formula={formula} />
            ))}
          </div>
          <p className="text-[11px] font-['Manrope'] text-white/35 mt-4 leading-relaxed">
            {report.extension_notes}
          </p>
        </div>
      </div>
    </div>
  );
}